import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Operation } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { ChevronDownIcon, ChevronUpIcon, OperationsIcon, TrashAltIcon } from 'assets/icons';
import { removeOperation, updateOperation } from 'domains/estimate/Estimate.store';
import { getSparePartsView } from 'domains/user';
import TableSection from 'pages/EstimatePage/TableSection/TableSection';
import { CollapsibleContent } from 'pages/EstimatePage/TableSection/TableSection.styled';
import { theme } from 'styles';
import { Flex, Icon, Input, Text } from 'UI';
import { useFocus } from 'utils';

const EstimateOperationSection = ({ estimateId, operation }: { estimateId: string; operation: Operation }) => {
  const dispatch = useDispatch();
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const inputRef = useRef(null);
  const isEasyBasketOperation = operation.operationType === 'EASY_BASKET';
  const sparePartsView = useSelector(getSparePartsView);

  const handleRemoveOperation = () => {
    dispatch(removeOperation(operation.itemId));
  };

  useFocus(inputRef, [isNameEditing]);

  const handleChangeName = (value: string) => {
    setIsNameEditing(false);
    if (value.trim().length > 0) {
      dispatch(
        updateOperation({
          itemId: operation.itemId,
          newValue: value,
          field: 'NAME',
        }),
      );
    }
  };

  const renderRemoveOperation = () => (
    <Icon
      IconComponent={TrashAltIcon}
      color={theme.color.clear_blue}
      size={20}
      onClick={handleRemoveOperation}
      data-cy={'remove-operation-button'}
    />
  );

  const renderOperationName = () => {
    if (isNameEditing) {
      return <Input placeholder={operation.name} passRef={inputRef} bordered onBlur={handleChangeName} />;
    } else {
      return (
        <>
          <Text type="heading3" disableGutter noWrap transform={'capitalize'}>
            {operation.name}
          </Text>
          {renderRemoveOperation()}
        </>
      );
    }
  };

  return (
    <Flex direction={'column'}>
      <Flex align={'center'} gap={8}>
        <Flex size={0} align={'center'} gap={8} minWidth={isNameEditing ? '50%' : 'auto'}>
          <Icon
            IconComponent={collapsed ? ChevronDownIcon : ChevronUpIcon}
            size={22}
            onClick={() => setCollapsed((prev) => !prev)}
            mt={3}
            data-cy={'estimate-operation-collapse-toggle'}
          />
          {isEasyBasketOperation && <Icon IconComponent={OperationsIcon} size={28} noPointer />}
          {collapsed ? (
            <Flex align={'center'} gap={10} minHeight={38}>
              <Text type="heading3" disableGutter noWrap transform={'capitalize'}>
                {operation.name}
              </Text>
              {renderRemoveOperation()}
            </Flex>
          ) : (
            <Flex align={'center'} gap={10} onClick={() => setIsNameEditing(true)} minHeight={38}>
              {renderOperationName()}
            </Flex>
          )}
        </Flex>
      </Flex>
      <CollapsibleContent $collapsed={collapsed} $margin={'0 0 5px 0'}>
        <div>
          <Flex direction={'column'} gap={10}>
            <TableSection
              estimateId={estimateId}
              type={'operation_reference'}
              operationId={operation.itemId}
              sparePartsView={sparePartsView}
            />
            <TableSection
              estimateId={estimateId}
              type={'operation_labor_time'}
              operationId={operation.itemId}
              sparePartsView={sparePartsView}
            />
          </Flex>
        </div>
      </CollapsibleContent>
    </Flex>
  );
};

export default EstimateOperationSection;
